/**
 * Battle Casualty Module - PSP/CRE Tool v3.0
 *
 * Handles core battle casualty calculations including:
 * - Base casualty rates by operational posture
 * - Unit type modifiers
 * - Terrain/weather/threat effects
 * - Modern warfare patterns (Ukraine 2022-2024)
 * - Force quality effects
 * - Day-by-day fatigue modeling
 * - Mission role modifiers (BUG-002 FIX)
 * - Monte Carlo uncertainty modifiers (Phase 1.2)
 *
 * @version 5.9.0
 * @date March 27, 2026
 *
 * VERSION 5.9.0 - ENH-042: Per-brigade engagement_level wired (March 27, 2026)
 *   casualtyCalculator v5.31.0 (ENH-028/BUG-ENG) set brigade.operation.engagement_level from
 *   scenario.operation_parameters.engagement_level, but battleCasualtyModule never read it —
 *   engagement was still hardcoded 'ACTIVE' at line 1079.
 *   'ACTIVE' does not exist in ENGAGEMENT_LEVELS (keys: FULL/SUPPORT/RESERVE/SECURITY) →
 *   ENGAGEMENT_LEVELS['ACTIVE']?.casualty_multiplier was always undefined → fallback 1.0 fired 100%.
 *   Fix: read brigade?.operation?.engagement_level, fall back to 'FULL' (1.0× — same effective
 *   behaviour as the broken 'ACTIVE' hardcode, so backward compatible).
 *   ENH-042 result: SUPPORT=0.3×, RESERVE=0.0×, SECURITY=0.1× now actually applied.
 *
 * VERSION 5.8.0 - ENH-029: Context-derived injury mechanism in calculateDailyBattleCasualties (March 10, 2026)
 *   Cat-29 of §20 Research Inventory Audit — mechanism was hardcoded 'ARTILLERY_MORTAR' in
 *   calculateCasualtyDistribution call inside calculateDailyBattleCasualties, meaning KIA%,
 *   T1%, and T4% never varied by terrain or mission type.
 *
 *   - NEW: getDominantMechanism(terrain, missionType) helper — maps context to canonical key:
 *       URBAN/CITY terrain           → SMALL_ARMS   (15% KIA, 25% T1)
 *       ATTACK/OFFENSE/MVT missions  → SMALL_ARMS   (assault close-combat pattern)
 *       DEFENSE missions             → MINES_IED    (22% KIA, 55% T1; mines dominate)
 *       Default (open/mixed)         → ARTILLERY_MORTAR (unchanged baseline)
 *   - calculateDailyBattleCasualties now reads brigade.environment?.terrain and
 *     brigade.operation?.mission_type (both already passed by calculator but not read).
 *   - brigTerrain replaces hardcoded 'MIXED' in calculateBaseCasualtyRate → terrain
 *     multipliers now apply correctly (terrain was always MIXED before — pre-existing bug).
 *   - missionType replaces hardcoded 'GROUND_COMBAT' → isDefensive terrain-defense modifier
 *     now activates for defensive postures (was always false before — pre-existing bug).
 *   Backward-compat: missing fields → ARTILLERY_MORTAR (same as previous hardcode).
 *
 * VERSION 5.7.0 - ENH-026 + BUG-FSR: Force superiority — quality-adjusted ratio (March 10, 2026)
 *   BUG-FSR (silent): applyForceSuperiorityModifier() returned `friendly_modifier` but
 *   calculator read `forceSuperiorityResult.modifier` — always undefined → 1.0.
 *   Fixed by adding `modifier: friendlyMod` to return object.
 *   ENH-026: Quality-adjusted effective strength (Dupuy CEV). effectiveStrength = raw × CEV.
 *   CEV from TRAINING_LEVELS.effectiveness_value. Backward-compat: missing → CEV 1.0.
 *
 * VERSION 5.6.0 - ENH-022: Frontage density modifier (March 10, 2026)
 *   Category 22 of §20 Research Inventory Audit — was "UI only, no calc modifier".
 *   - NEW IMPORT: getDensityModifier from frontageDensity.js (new constants file v1.0.0)
 *   - calculateDailyBattleCasualties: reads brigade.position?.frontage_width_km.
 *     Computes densityResult = getDensityModifier(strength, frontage_width_km).
 *     Applies BEFORE calculateCasualtyDistribution:
 *       densityAdjustedCasualties = Math.round(rawCasualties * densityResult.modifier)
 *     KIA/WIA/DOW split is thus proportionally scaled by density.
 *   - Return object: new `density_modifier` block (band, troops_per_km, modifier, warning).
 *   - Units with frontage_width_km = 0 (support, logistics, medical, MARCHING):
 *     getDensityModifier returns modifier=1.0, applicable=false. No effect.
 *   - 5 bands: VERY_DISPERSED 0.70× / DISPERSED 0.85× / NORMAL 1.0× /
 *     CONCENTRATED 1.20× / VERY_CONCENTRATED 1.40×.
 *   Source: Dupuy TNDM, Ukraine 2022-2024 ISW/JHU APL (3–4× casualty difference),
 *           AJMedP-1 §4.2, ATP 4-02.2 §2-8.
 *
 * VERSION 5.5.0 - ENH-BAP: Body armor KIA reduction (March 10, 2026)
 *   Category 20 of Research Inventory Audit — body armor was the only 0% genuine gap.
 *   - NEW IMPORT: BODY_ARMOR_LEVELS, getArmorKIAReduction from bodyArmorProtection.js
 *   - calculateCasualtyDistribution: adds armor_level param (default 'LEVEL_III').
 *     After TCCC modifier: kiaRate *= (1 − armorKIAReduction). Emits body_armor block.
 *   - calculateDailyBattleCasualties: adds armorLevel param (default 'LEVEL_III').
 *     Passed through to calculateCasualtyDistribution armor_level option.
 *   Source: ATP 4-02.2 §3-14, AJMedP-1 Annex C, Ukraine 2022-2024 ISW/JHU APL data.
 *   Levels: NONE (0%) / IIA (5%) / IIIA (8%) / III (15%, NATO default) / IV (20%, US Army).
 *
 * VERSION 5.4.0 - BCM-05/06/12/13/14/15 constants migration (February 24, 2026)
 * - BCM-05/06: Local CASUALTY_MECHANISMS deleted. CASUALTY_MECHANISMS imported from
 *   casualtyCalculatorConstants.js v2.2. canonical now has t4_percentage field (added
 *   in CCC v2.2). MECH_KEY_MAP adapter normalises local keys (ARTILLERY_MORTAR→ARTILLERY,
 *   FPV_DRONES→FPV_DRONE) at lookup site. Field reads updated:
 *     .kia_rate → .kia_percentage, .t1_rate → .t1_percentage, .t4_rate → .t4_percentage
 * - BCM-12: `* 0.8` → `* ENEMY_EXCHANGE_BASELINE` (0.80, from CCC v2.2)
 * - BCM-13: FST literals replaced:
 *     0.83 → FST_DOW_REDUCTION, 0.25 → FST_T4_CONVERSION, 0.6 → FST_T4_TO_T1_RATIO
 * - BCM-14: Golden hour literals replaced:
 *     0.60 → GOLDEN_HOUR_BASELINE, 0.30 → GOLDEN_HOUR_KIA_FACTOR,
 *     0.85 → GOLDEN_HOUR_CLAMP_MIN, 1.15 → GOLDEN_HOUR_CLAMP_MAX
 * - BCM-15: Surgical literals replaced:
 *     0.4 → SURGICAL_RATE_OVERALL, 2 → SURGICAL_HOURS_PER_CASE,
 *     8 → SURGICAL_HOURS_PER_TEAM_DAY
 *   assessMASCALCondition() signature unchanged (aggregate totalCasualties).
 *   If refactored for triage breakdown: migrate to surgicalCapacity.js (Phase 5+).
 * @version 5.3.0
 * @date February 24, 2026
 *
 * VERSION 5.3.0 - BCM-07/08/11 fixes (February 24, 2026)
 * - BCM-07: Deleted local `NIGHT_BATTLE_MODIFIERS` block. Imported from
 *   casualtyCalculatorConstants.js v2.1 (single usage: casualty_rate_modifier).
 * - BCM-08: Deleted local `URBAN_WARFARE_MODIFIERS` block — dead code, zero
 *   usage sites in this module. Not migrated to constants since nothing reads it.
 * - BCM-11: Fixed `RATIO_TO_CONST_KEY` stale key mapping. Previous keys
 *   (SIGNIFICANT, SLIGHT, SLIGHT_ENEMY, SIGNIFICANT_ENEMY, OVERWHELMING_ENEMY)
 *   don't exist in FORCE_SUPERIORITY — caused getSuperiorityMod() to always
 *   fall through to hardcoded fallback block. Fixed to correct canonical keys:
 *   2_TO_1→SUPERIOR, 1.5_TO_1→SUPERIOR, 1_TO_1.5→INFERIOR, 1_TO_2→INFERIOR,
 *   1_TO_3→SEVERELY_INFERIOR. Fallback block deleted — now unreachable.
 *   getSuperiorityMod() now throws on unknown ratio key (APP_RULES 6.1).
 *
 * STILL DEFERRED:
 * - BCM-05/06: CASUALTY_MECHANISMS key+field mismatch — local ARTILLERY_MORTAR/
 *   FPV_DRONES + .kia_rate/.t1_rate vs canonical ARTILLERY/FPV_DRONE +
 *   .kia_percentage/.t1_percentage. Usage fallback `CASUALTY_MECHANISMS.ARTILLERY_MORTAR`
 *   doesn't exist in canonical. Requires refactor of calculateCasualtyDistribution().
 * - BCM-12: `* 0.8` enemy exchange baseline — ENEMY_EXCHANGE_RATIO doesn't exist
 *   in combatEffectivenessModifiers.js. FORCE_SUPERIORITY.EQUAL.enemy_casualty_multiplier
 *   = 1.0 (different value, changes results). Deferred.
 * - BCM-13/14/15: FST and golden hour literals — FST_DOW_REDUCTION etc not yet in
 *   casualtyCalculatorConstants.js.
 *
 * VERSION 5.2 - Phase 3-A BCM-09/10 constants migration (February 22, 2026)
 * @author Dr. Tatar Radu MD
 *
 * VERSION 5.2 - Phase 4-B partial constants migration (February 22, 2026)
 * - BCM-09: Deleted local `postureModifiers` block inside calculateUnitTypeCasualties().
 *   OPERATIONAL_POSTURES from operationsConfig.js was already imported and has
 *   casualty_multiplier for all posture keys. Local block had DIFFERENT values
 *   (e.g. DELIBERATE_DEFENSE: 0.7 local vs 0.6 canonical, RETIREMENT: 0.5 local vs 0.1
 *   canonical, RESERVE: 0.2 local vs 0.0 canonical) — it was a wrong-values fallback.
 *   Lookup now: `OPERATIONAL_POSTURES?.[normalizedPosture]?.casualty_multiplier ?? 1.0`
 * - BCM-10: MISSION_ROLES constant + getMissionRoleMultiplier() moved to operationsConfig.js.
 *   battleCasualtyModule.js now imports both and re-exports for backward compatibility.
 *
 * DEFERRED items (canonical source files not yet available):
 * - BCM-05/06: ✅ DONE in v5.4.0 — see VERSION 5.4.0 above.
 * - BCM-07: ✅ DONE in v5.3.0 — NIGHT_BATTLE_MODIFIERS imported from CCC.
 * - BCM-08: ✅ DONE in v5.3.0 — URBAN_WARFARE_MODIFIERS dead code removed.
 * - BCM-11: Still deferred — combatEffectivenessModifiers.js fallback block.
 * - BCM-12: ✅ DONE in v5.4.0 — see VERSION 5.4.0 above.
 * - BCM-13: ✅ DONE in v5.4.0 — see VERSION 5.4.0 above.
 * - BCM-14: ✅ DONE in v5.4.0 — see VERSION 5.4.0 above.
 * - BCM-15: ✅ DONE in v5.4.0 — see VERSION 5.4.0 above. Function signature unchanged.
 * 
 * VERSION 5.1 CHANGES (February 22, 2026) — Phase 0-B Architecture:
 * - BCM-01: Added NATIONAL_STANDARDS to import from nationalStandards.js
 * - BCM-02: Deleted local NATIONAL_REAR_AREA_MODIFIERS constant block — values now live
 *   in NATIONAL_STANDARDS[nation].rear_area_security_modifier (per NS-02)
 * - BCM-03: calculateRearAreaCasualties() — replaced NATIONAL_REAR_AREA_MODIFIERS[nation]
 *   lookup with NATIONAL_STANDARDS[nationalStandard]?.rear_area_security_modifier ?? 1.00
 *   Single source of truth now in nationalStandards.js. Supports all 7 nations including
 *   GERMAN (0.90), BRITISH (0.92), POLISH (0.88) added in Phase 0-A.
 * - BCM-04: Fixed wrong getHQCasualtyModifier() call (was passing threatLevel as first arg
 *   instead of nationalStandard). Function signature is (nationalStandard, echelon).
 *   Bug caused modifier to always return undefined → fallback 1.0 fired 100% of the time.
 *   Fixed to: getHQCasualtyModifier(nationalStandard, 'corps')
 *
 * VERSION 5.0.1 CRITICAL PATH FIX (February 16, 2026):
 * - BUG FIX: Monte Carlo modifier was reading from wrong location
 * - OLD: scenario.monte_carlo_modifiers.casualty_rate_modifier (doesn't exist)
 * - NEW: scenario.operation_parameters.monte_carlo_modifiers.casualty_rate_modifier
 * - IMPACT: Modifier always defaulted to 1.0 → no variance in Monte Carlo results
 * - RESULT: Now reads correct path → percentiles will show real variance
 * 
 * VERSION 5.0 PHASE 1.2: Monte Carlo Modifier Integration (February 16, 2026):
 * - NEW: Reads scenario.monte_carlo_modifiers.casualty_rate_modifier
 * - NEW: Applies Monte Carlo variance to base casualty rate calculations
 * - LOCATION: calculateBaseCasualtyRate() line ~640
 * - IMPACT: Enables uncertainty quantification for casualty projections
 * - PATTERN: Modifier = 1.0 (deterministic), 0.8-1.2 range (Monte Carlo ±20%)
 * - INTEGRATION: Works with monteCarloSimulator.js VARIANCE_PARAMETERS
 *
 * VERSION 4.9 FIXES (January 31, 2026):
 * - TASK 3.0 FIX: Corrected operational_state fallback values
 *   - SUPPORT_ACTIVE: 0.15 → 0.2 (was 25% too low)
 *   - TRUE_RESERVE: 0.02 → 0.3 (was 93% too low!)
 *   - Added explicit TRUE_RESERVE case in fallback
 *   - Default fallback: 1.0 (Combat Engaged) instead of 0.02
 *   - CRITICAL: If imports fail, fallbacks now use correct multipliers
 *   - Ensures Support Active has 5x fewer casualties than Combat Engaged
 *   - Ensures True Reserve has 3.3x fewer casualties than Combat Engaged
 * 
 * VERSION 4.8 FIXES (January 21, 2026):
 * - BUG-044 FIX: assessMASCALCondition() crash on undefined thresholds
 *   - MASCAL_THRESHOLDS imported but has different structure than expected
 *   - `{} || fallback` doesn't trigger because {} is truthy
 *   - `{}.mtf_utilization.critical` → TypeError
 *   - SOLUTION: Built thresholds with nullish coalescing (??) for each property
 *   - Checks both lowercase and UPPERCASE property names for compatibility
 *   - Falls back to sensible defaults if constant unavailable
 *
 * VERSION 4.7 FIXES (January 21, 2026):
 * - BUG-043 FIX: calculateDailyBattleCasualties() return structure mismatch
 *   - casualtyCalculator.js expects `battleResult.battle_casualties.total`
 *   - Module was returning `casualties` instead of `battle_casualties`
 *   - Now returns BOTH for compatibility:
 *     - `battle_casualties` - for casualtyCalculator.js
 *     - `casualties` - for backward compatibility
 *
 * VERSION 4.6 FIXES (January 21, 2026):
 * - ESLINT-010: hasForwardSurgical now USED in calculateCasualtyDistribution()
 *   - FST reduces DOW by 17% (faster damage control surgery)
 *   - FST converts 25% of T4 patients to salvageable (T1/T2)
 *   - Added forward_surgical object to return value
 *   - Educational note explains FST impact
 * - ESLINT-011: nationalStandard now USED in calculateRearAreaCasualties()
 *   - Different national standards have different rear area protection
 *   - US: 20% better (robust RASO doctrine)
 *   - NATO: baseline
 *   - ROMANIAN: 5% better (territorial defense integration)
 *   - Added national_standard object to return value
 *
 * VERSION 4.5 FIXES (January 21, 2026):
 * - BUG-034 FIX: Posture passed as object instead of string
 *   - ROOT CAUSE: Tab 2 saves posture as {value: 'X', label: 'Y'} object
 *   - Calculator expects string 'X'
 *   - SYMPTOM: "Unknown posture: [object Object]" warnings, all calculations return NaN
 *   - SOLUTION: Added normalizePosture() helper function
 *   - Normalizes posture to string before any lookup
 *   - Handles: string, object with .value, object with .id, undefined, null
 *   - Applied in: calculateBaseCasualtyRate(), calculateUnitTypeCasualties()
 *
 * VERSION 4.4 FIXES (January 20, 2026):
 * - BUG-031 FIX: applyForceSuperiorityModifier() null pointer crash
 *   - Problem 1: Key mismatch - code used '1_TO_1', constants use 'EQUAL'
 *   - Problem 2: Property mismatch - code expected 'friendly_mod', 
 *                constants have 'friendly_casualty_multiplier'
 *   - Solution: Added RATIO_TO_CONST_KEY mapping and getSuperiorityMod() helper
 *   - Now correctly reads FORCE_SUPERIORITY.EQUAL.friendly_casualty_multiplier
 *   - Falls back to sensible defaults if constants unavailable
 *
 * VERSION 4.3 FIXES (January 18, 2026):
 * - ESLINT-001: getRearCasualtyRate now USED in calculateRearAreaCasualties()
 * - ESLINT-002: getEnemyCasualtyModifier now USED in calculateDailyBattleCasualties()
 * - ESLINT-003: assessBreakProbability now USED in calculateDailyBattleCasualties()
 * - ESLINT-004: getHQCasualtyModifier now USED in calculateRearAreaCasualties()
 * - ESLINT-005: getEchelonBreakdown now USED in new calculateEchelonCasualties()
 * - ESLINT-006: goldenHourAchievement now USED to modify KIA/T4 rates
 * - ESLINT-007: enemyQuality now USED for enemy casualty calculation
 * - ESLINT-008: tcccAnalysis now USED to modify survival rates
 * - ESLINT-009: threatLevel now USED in calculateEchelonCasualties() for HQ targeting
 * - Fixed duplicate export error for MISSION_ROLES
 * 
 * VERSION 4.2 FIXES (January 18, 2026):
 * - BUG-002 FIX: Mission roles now implemented
 *   - Added MISSION_ROLES constant with multipliers
 *   - Added getMissionRoleMultiplier() helper function
 *   - calculateBaseCasualtyRate() now accepts missionRole parameter
 *   - Combined modifier now includes missionRoleMultiplier
 *   - MAIN_EFFORT: 1.5× (primary attacking force)
 *   - SUPPORTING: 1.0× (supports main effort)
 *   - RESERVE: 0.2× (held in reserve, out of contact)
 *   - EXPLOITATION: 1.3× (exploiting breakthrough)
 *   - ECONOMY_OF_FORCE: 0.6× (minimal force commitment)
 * 
 * VERSION 4.1 FIXES (January 7, 2025):
 * - FIX-BCM-001: calculateMedicalPersonnelCasualties now uses dayOfOperation parameter
 *   - Applies fatigue factor via getFatigueFactor() or fallback calculation
 *   - Day 1 = 1.0x, Day 14+ = 1.42x (max +42% risk increase)
 * - FIX-BCM-002: calculateUnitTypeCasualties now uses posture parameter
 *   - Attackers: HASTY_ATTACK 1.5x, ATTACK 1.3x, DELIBERATE_ATTACK 1.2x
 *   - Defenders: DELIBERATE_DEFENSE 0.7x, AREA_DEFENSE 0.65x, DEFEND 0.8x
 *   - Fixes critical doctrine violation where attacker/defender had same rates
 */

// ============================================================================
// IMPORTS
// ============================================================================

import {
  BASE_CASUALTY_RATE,
  MASCAL_THRESHOLDS,
  REAR_AREA_CASUALTIES,
  THREAT_TO_REAR_MAP,
  MEDICAL_PERSONNEL_VULNERABILITY,
  FATIGUE_FACTORS,
  MODERN_WARFARE_MULTIPLIERS,
  CASUALTY_DISTRIBUTION,
  NIGHT_BATTLE_MODIFIERS,         // BCM-07: canonical (v2.2)
  CASUALTY_MECHANISMS,             // BCM-05/06: canonical (v2.2)
  FST_DOW_REDUCTION,               // BCM-13: 0.83
  FST_T4_CONVERSION,               // BCM-13: 0.25
  FST_T4_TO_T1_RATIO,             // BCM-13: 0.60
  GOLDEN_HOUR_BASELINE,            // BCM-14: 0.60
  GOLDEN_HOUR_KIA_FACTOR,          // BCM-14: 0.30
  GOLDEN_HOUR_CLAMP_MIN,           // BCM-14: 0.85
  GOLDEN_HOUR_CLAMP_MAX,           // BCM-14: 1.15
  ENEMY_EXCHANGE_BASELINE,         // BCM-12: 0.80
  SURGICAL_RATE_OVERALL,           // BCM-15: 0.40
  SURGICAL_HOURS_PER_CASE,         // BCM-15: 2
  SURGICAL_HOURS_PER_TEAM_DAY,     // BCM-15: 8
  getFatigueFactor,
  getRearCasualtyRate,
  getModernWarfareMultiplier
} from '../../constants/casualtyCalculatorConstants.js';

// CALC-010/012: required-constant reader (throws with the path instead of a literal fallback)
import { requireNumber } from '../requireValue.js';

import {
  BODY_ARMOR_LEVELS,
  getArmorKIAReduction
} from '../../constants/bodyArmorProtection.js';

import {
  OPERATION_TYPES,
  OPERATIONAL_POSTURES,
  ENGAGEMENT_LEVELS,
  TERRAIN_TYPES,
  WEATHER_CONDITIONS,
  THREAT_LEVELS,
  MISSION_ROLES,            // BCM-10: Moved from local block → operationsConfig.js
  getMissionRoleMultiplier  // BCM-10: Moved from local function → operationsConfig.js
} from '../../constants/operationsConfig.js';

import {
  TRAINING_LEVELS,
  EQUIPMENT_LEVELS,
  MORALE_LEVELS,
  FORCE_SUPERIORITY,
  EXPERIENCE_MODIFIERS,
  TECHNOLOGY_ENVIRONMENT,
  MEDICAL_CAPABILITY,
  calculateForceQualityModifier,
  getEnemyCasualtyModifier,
  assessBreakProbability
} from '../../constants/combatEffectivenessModifiers.js';

import {
  UNIT_TYPES,
  getCasualtyModifier as getUnitTypeCasualtyModifier
} from '../../constants/unitTypes.js';

import {
  NATIONAL_STANDARDS,      // BCM-01: Added — provides rear_area_security_modifier per nation
  getHQCasualtyModifier,
  getEchelonBreakdown,
  getTerrainDefenseModifier
} from '../../constants/nationalStandards.js';

import {
  getBattleCasualtyMultiplier
} from '../../constants/operationalStates.js';

// ENH-022: Frontage density modifier (Cat-22 Research Inventory Audit)
import { getDensityModifier } from '../../constants/frontageDensity.js';

// ============================================================================
// BUG-034 FIX: POSTURE NORMALIZER
// ============================================================================

/**
 * BUG-034 FIX: Normalize posture to a string value
 * 
 * Problem: Tab 2 saves posture as an object {value: 'DELIBERATE_DEFENSE', label: '...'}
 * but calculator expects just the string 'DELIBERATE_DEFENSE'.
 * 
 * This causes OPERATIONAL_POSTURES[posture] to return undefined,
 * leading to undefined * number = NaN propagating through all calculations.
 * 
 * @param {string|Object} posture - The posture value (may be object or string)
 * @param {string} defaultPosture - Default if posture is invalid
 * @returns {string} - Normalized posture string (e.g., 'DELIBERATE_DEFENSE')
 * 
 * @example
 * normalizePosture('ATTACK') // Returns 'ATTACK'
 * normalizePosture({value: 'ATTACK', label: 'Attack'}) // Returns 'ATTACK'
 * normalizePosture(undefined) // Returns 'DELIBERATE_DEFENSE'
 * normalizePosture(null) // Returns 'DELIBERATE_DEFENSE'
 */
function normalizePosture(posture, defaultPosture = 'DELIBERATE_DEFENSE') {
  // Case 1: Already a valid string
  if (typeof posture === 'string' && posture.length > 0) {
    return posture;
  }
  
  // Case 2: Object with .value property (MUI Select pattern)
  if (posture && typeof posture === 'object') {
    // Try .value first (most common)
    if (posture.value && typeof posture.value === 'string') {
      return posture.value;
    }
    // Try .id (alternative pattern)
    if (posture.id && typeof posture.id === 'string') {
      return posture.id;
    }
    // Try .key (another alternative)
    if (posture.key && typeof posture.key === 'string') {
      return posture.key;
    }
    // Try .type (yet another alternative)
    if (posture.type && typeof posture.type === 'string') {
      return posture.type;
    }
  }
  
  // Case 3: Invalid - use default
  // Only log warning for truly unexpected values (not undefined from missing config)
  if (posture !== undefined && posture !== null) {
  }
  return defaultPosture;
}

/**
 * BUG-034 FIX: Export normalizePosture for use in other modules
 * Other modules (like casualtyCalculator.js) may also receive posture as object
 */
export { normalizePosture };

// ============================================================================
// CONSTANTS - From research, NO HARDCODING
// ============================================================================

// BCM-10: MISSION_ROLES and getMissionRoleMultiplier moved to operationsConfig.js
// (single source of truth). Imported above and re-exported here for backward
// compatibility — existing code importing from battleCasualtyModule.js still works.
export { MISSION_ROLES, getMissionRoleMultiplier };

/**
 * BCM-06 FIX (February 24, 2026): Local CASUALTY_MECHANISMS deleted.
 * Imported from casualtyCalculatorConstants.js v2.2. Field name changes:
 *   .kia_rate  → .kia_percentage  (all 1 usage site in calculateCasualtyDistribution)
 *   .t1_rate   → .t1_percentage   (1 usage site)
 *   .t4_rate   → .t4_percentage   (1 usage site — field added to canonical in BCM-05)
 * Key name changes at lookup site:
 *   fallback ARTILLERY_MORTAR → ARTILLERY
 *   caller passes 'ARTILLERY_MORTAR' → normalised inside lookup via key adapter
 * NOTE: canonical also has VEHICLE_STRIKE key (not in local) and richer body_regions data.
 */

/**
 * BCM-07 FIX: Local NIGHT_BATTLE_MODIFIERS deleted — imported from
 * casualtyCalculatorConstants.js v2.1. Single usage: casualty_rate_modifier.
 *
 * BCM-08 FIX: Local URBAN_WARFARE_MODIFIERS deleted — dead code (zero usage
 * sites in this module). Not migrated to casualtyCalculatorConstants.js since
 * nothing reads it. Removed to eliminate orphan constant.
 */

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get enhanced unit type configuration
 * 
 * @param {string} unitType - Type of unit (MECHANIZED, INFANTRY, etc.)
 * @returns {Object} Unit configuration with modifiers
 */
export function getEnhancedUnitType(unitType) {
  // Try to get from constants first
  const baseConfig = UNIT_TYPES?.[unitType] || null;
  
  // Get modifier from helper function if available
  const modifier = getUnitTypeCasualtyModifier?.(unitType) || 
                   baseConfig?.casualty_modifier || 
                   1.0;
  
  // Enhanced configurations based on Ukraine 2022-2024 data
  const enhancements = {
    MECHANIZED: {
      modifier: 1.0,
      vehicle_loss_rate: 0.03,
      dismount_vulnerability: 1.4,
      drone_vulnerability: 1.2
    },
    ARMOR: {
      modifier: 0.85,
      vehicle_loss_rate: 0.04,
      dismount_vulnerability: 1.6,
      drone_vulnerability: 1.5,       // Higher due to top-attack drones
      ukraine_note: 'Tank losses from FPV drones exceeded expectations'
    },
    INFANTRY: {
      modifier: 1.15,
      drone_vulnerability: 1.0,
      artillery_vulnerability: 1.2,
      cover_utilization: 1.3
    },
    AIRBORNE: {
      modifier: 1.25,
      initial_insertion_risk: 2.5,    // Very high during insertion
      after_consolidation: 1.1,
      ukraine_example: 'Hostomel airborne assault'
    },
    AIR_ASSAULT: {
      modifier: 1.2,
      heli_insertion_risk: 1.8,
      after_insertion: 1.0
    },
    MOUNTAIN: {
      modifier: 1.1,
      altitude_sickness_risk: 0.05,
      evacuation_difficulty: 1.6
    },
    ARTILLERY: {
      modifier: 0.4,                  // Lower base rate
      counter_battery_vulnerability: 2.5,  // Very high if detected
      ukraine_note: 'Counter-battery became dominant threat'
    },
    LOGISTICS: {
      modifier: 0.3,
      rear_area_vulnerability: 1.8,   // Drone/long-range fires
      convoy_risk: 2.0
    },
    MEDICAL: {
      modifier: 0.25,
      deliberate_targeting_modifier: 1.5,  // Ukraine showed targeting
      evacuation_exposure: 1.3
    },
    HQ: {
      modifier: 0.2,
      signature_vulnerability: 2.5,   // High-value target
      ukraine_note: 'HQ targeting via signals intelligence'
    },
    ENGINEERS: {
      modifier: 0.6,
      breaching_vulnerability: 2.0,   // Very high during breaching ops
      mine_clearing_risk: 1.8
    },
    SIGNAL: {
      modifier: 0.25,
      signature_vulnerability: 2.0,   // Targeted via EW
      ukraine_note: 'Signal units targeted via direction-finding'
    },
    AIR_DEFENSE: {
      modifier: 0.3,
      sead_vulnerability: 3.0,        // Primary SEAD target
      ukraine_note: 'AD systems high-priority targets'
    },
    RECONNAISSANCE: {
      modifier: 0.8,
      forward_exposure: 1.5,
      small_unit_vulnerability: 1.2
    }
  };
  
  const enhanced = enhancements[unitType] || { modifier: modifier };
  
  return {
    unit_type: unitType,
    base_config: baseConfig,
    modifier: enhanced.modifier || modifier,
    ...enhanced
  };
}

// ============================================================================
// CORE CALCULATION FUNCTIONS
// ============================================================================

// 2026-10-18: see the comment at terrainDefenseMod in calculateBaseCasualtyRate.
export const TERRAIN_DEFENSE_FACTOR_ACTIVE = false;

/**
 * Calculate base casualty rate with all modifiers
 * 
 * BUG-002 FIX: Now includes missionRole parameter for brigade mission role modifier
 * BUG-034 FIX: Now normalizes posture before lookup (handles object or string)
 * 
 * @param {Object} config - Configuration object
 * @param {string} config.missionType - Type of mission (GROUND_COMBAT, URBAN_COMBAT, etc.)
 * @param {string|Object} config.posture - Operational posture (ATTACK, DEFEND, etc.) - BUG-034: can be object or string
 * @param {string} config.engagement - Engagement level (ACTIVE, LIGHT, etc.)
 * @param {string} config.terrain - Terrain type (MIXED, URBAN, etc.)
 * @param {string} config.weather - Weather conditions (FAIR, ADVERSE, etc.)
 * @param {string} config.threatLevel - Threat level (LOW, PEER, etc.)
 * @param {string} config.unitType - Unit type (MECHANIZED, INFANTRY, etc.)
 * @param {string} config.operationalState - Operational state (COMBAT_ENGAGED, etc.)
 * @param {string} config.missionRole - BUG-002 FIX: Brigade mission role (MAIN_EFFORT, SUPPORTING, RESERVE, etc.)
 * @param {number} config.day - Day of operation
 * @param {boolean} config.isNight - Whether operation is at night
 * @returns {Object} Casualty rate analysis
 */
export function calculateBaseCasualtyRate(config) {
  const {
    missionType = 'MAIN_DEFENSE',
    posture = 'DELIBERATE_DEFENSE',
    engagement = 'ACTIVE',
    terrain = 'MIXED',
    weather = 'FAIR',
    threatLevel = 'PEER',
    unitType = 'MECHANIZED',
    operationalState = 'COMBAT_ENGAGED',
    missionRole = 'SUPPORTING',  // BUG-002 FIX: Added mission role with default
    operationType,               // Tab 2 operation type (OPERATION_TYPES key); when absent the legacy lookup by missionType applies
    phaseProfile = null,         // 2026-10-13: resolved by phaseCasualtyTable.resolvePhaseProfile; replaces operation type x posture x role
    day = 1,
    isNight = false,
    scenario = null  // PHASE 1.2: Added for Monte Carlo modifier access
  } = config;

  // BUG-034 FIX: Normalize posture to string before lookup
  const normalizedPosture = normalizePosture(posture, 'DELIBERATE_DEFENSE');

  // Get base rate from constants
  // CALC-012 (2026-10-09): was BASE_CASUALTY_RATE.per_day_combat || 0.02 - that key never existed, so
  // an unsourced 2% literal was used while the constant (Dupuy baseline, 1.5%/day) was ignored.
  // Dr. Tatar chose the sourced 1.5% constant. Dupuy's WWII division engagements: ~1-3%/day.
  const baseCasualtyRate = requireNumber(BASE_CASUALTY_RATE?.daily,
    'battleCasualtyModule.calculateBaseCasualtyRate', 'BASE_CASUALTY_RATE.daily');

  // Get modifiers from constants - BUG-034 FIX: Use normalizedPosture
  // 2026-10-11: the casualty multiplier of the operation (urban combat 1.4, river crossing 1.5 ...) is keyed by the
  // Tab 2 operation type. The legacy lookup by missionType never matched Tab 2's mission values (ATTACK, DEFEND ...).
  let operationTypeDef;
  if (operationType !== undefined) {
    operationTypeDef = OPERATION_TYPES?.[operationType];
    if (!operationTypeDef) {
      throw new Error(`battleCasualtyModule.calculateBaseCasualtyRate: unknown operation type '${operationType}' (valid: ${Object.keys(OPERATION_TYPES).join(', ')})`);
    }
  } else {
    operationTypeDef = OPERATION_TYPES?.[missionType] || { casualty_multiplier: 1.0 };
  }
  const postureType = OPERATIONAL_POSTURES?.[normalizedPosture] || { casualty_multiplier: 1.0 };
  const engagementType = ENGAGEMENT_LEVELS?.[engagement] || { casualty_multiplier: 1.0 };
  const terrainType = TERRAIN_TYPES?.[terrain] || { casualty_multiplier: 1.0 };
  const weatherType = WEATHER_CONDITIONS?.[weather] || { casualty_multiplier: 1.0 };
  const threatType = THREAT_LEVELS?.[threatLevel] || { casualty_multiplier: 1.0 };

  // Get unit type modifier
  const unitConfig = getEnhancedUnitType(unitType);

  // Get operational state modifier
  // TASK 3.0 FIX: Corrected fallback values to match operationalStates.js constants
  const stateMultiplier = getBattleCasualtyMultiplier?.(operationalState) || 
    (operationalState === 'COMBAT_ENGAGED' ? 1.0 : 
     operationalState === 'SUPPORT_ACTIVE' ? 0.2 :    // FIX: was 0.15
     operationalState === 'TRUE_RESERVE' ? 0.3 :      // FIX: was 0.02 (in else clause)
     1.0);  // Default to Combat Engaged for unknown states

  // BUG-002 FIX: Get mission role modifier
  const missionRoleMultiplier = getMissionRoleMultiplier(missionRole);

  // Get fatigue factor
  const fatigueFactor = getFatigueFactor?.(day) || 
    FATIGUE_FACTORS?.[`day_${Math.min(day, 14)}`] || 
    (1.0 + (Math.min(day - 1, 13) * 0.05));

  // Get modern warfare multiplier
  const modernMod = getModernWarfareMultiplier?.(threatLevel) || 
    MODERN_WARFARE_MULTIPLIERS?.[threatLevel] || 1.0;

  // Get terrain defense modifier if defending
  // 2026-10-18 (mission_type bug): getTerrainDefenseModifier() returns an OBJECT ({ modifier, description ... }) and was multiplied
  // into the combined rate as a number, so every call with a defensive mission type (the default 'MAIN_DEFENSE' when the scenario
  // has no mission_type) gave NaN casualties; it also read the attacker row (no posture passed). Every working flow had a mission
  // type outside this list and ran with the factor 1.0. TERRAIN_DEFENSE_FACTOR_ACTIVE = false keeps exactly that (factor 1.0, no
  // numbers move). Switching it on uses the defender row; the table (TERRAIN_DEFENSE_MODIFIERS) and its overlap with the terrain
  // multiplier and the urban condition are a pending decision of Dr. Tatar.
  const isDefensive = ['MAIN_DEFENSE', 'AREA_DEFENSE', 'MOBILE_DEFENSE', 'DELAY'].includes(missionType);
  let terrainDefenseMod = 1.0;
  if (TERRAIN_DEFENSE_FACTOR_ACTIVE && isDefensive && getTerrainDefenseModifier) {
    const _td = getTerrainDefenseModifier(terrain, missionType === 'DELAY' ? 'DELAY' : 'DEFENSE');
    terrainDefenseMod = typeof _td === 'number' ? _td : _td.modifier;
  }

  // Night modifier
  const nightMod = isNight ? NIGHT_BATTLE_MODIFIERS.casualty_rate_modifier : 1.0;

  // PHASE 1.2: Monte Carlo modifier (if present)
  // This modifier varies casualty rates within research-validated ranges (±20%)
  // Only present when Monte Carlo simulation is running (monteCarloSimulator.js)
  // CRITICAL FIX: Read from operation_parameters.monte_carlo_modifiers (not scenario root)
  const monteCarloMod = scenario?.operation_parameters?.monte_carlo_modifiers?.casualty_rate_modifier || 1.0;

  // Calculate combined modifier - BUG-002 FIX: Added missionRoleMultiplier
  // PHASE 1.2: Added monteCarloMod as final multiplier
  // 2026-10-13 (Tab 2 plan, step 2): with a phase profile ONE factor (form at the enemy resistance x maneuver group x
  // special condition, phaseCasualtyTable.js) replaces the operation type, the posture and (when the phase or sub-phase
  // names a role) the brigade role; each is counted once. The old multipliers are kept and used without a profile.
  const operationPostureMultiplier = phaseProfile
    ? phaseProfile.total_factor
    : operationTypeDef.casualty_multiplier * postureType.casualty_multiplier;
  const roleMultiplierApplied = (phaseProfile && phaseProfile.role_replaces_static) ? 1.0 : missionRoleMultiplier;
  const combinedModifier = 
    operationPostureMultiplier *
    engagementType.casualty_multiplier *
    terrainType.casualty_multiplier *
    weatherType.casualty_multiplier *
    threatType.casualty_multiplier *
    unitConfig.modifier *
    stateMultiplier *
    roleMultiplierApplied *  // BUG-002 FIX: Mission role now applied (replaced by the maneuver group when the phase names a role)
    fatigueFactor *
    modernMod *
    terrainDefenseMod *
    nightMod *
    monteCarloMod;  // PHASE 1.2: Monte Carlo uncertainty

  // Final rate
  const adjustedRate = baseCasualtyRate * combinedModifier;

  return {
    base_rate: baseCasualtyRate,
    adjusted_rate: Math.round(adjustedRate * 10000) / 10000,
    adjusted_rate_percent: Math.round(adjustedRate * 10000) / 100,
    
    modifiers: {
      mission: { type: operationType !== undefined ? operationType : missionType, value: operationTypeDef.casualty_multiplier },
      posture: { type: normalizedPosture, value: postureType.casualty_multiplier }, // BUG-034: Use normalized
      engagement: { type: engagement, value: engagementType.casualty_multiplier },
      terrain: { type: terrain, value: terrainType.casualty_multiplier },
      weather: { type: weather, value: weatherType.casualty_multiplier },
      threat: { type: threatLevel, value: threatType.casualty_multiplier },
      unit_type: { type: unitType, value: unitConfig.modifier },
      operational_state: { state: operationalState, value: stateMultiplier },
      mission_role: { role: missionRole, value: roleMultiplierApplied },  // BUG-002 FIX: Added to output
      phase_profile: phaseProfile,
      fatigue: { day: day, value: Math.round(fatigueFactor * 100) / 100 },
      modern_warfare: modernMod,
      terrain_defense: terrainDefenseMod,
      night: isNight ? nightMod : 1.0,
      combined: Math.round(combinedModifier * 100) / 100
    },
    
    unit_config: unitConfig,
    
    // BUG-034 FIX: Include normalization info for debugging
    posture_normalization: {
      original: posture,
      normalized: normalizedPosture,
      was_object: typeof posture === 'object'
    },
    
    // BUG-002 FIX: Educational information about mission role impact
    mission_role_analysis: {
      role: missionRole,
      multiplier: missionRoleMultiplier,
      description: MISSION_ROLES[missionRole]?.description || 'Unknown role',
      educational_note: missionRole === 'MAIN_EFFORT' 
        ? 'Main effort brigade bears highest casualties (~50% more than supporting)'
        : missionRole === 'RESERVE'
        ? 'Reserve brigade takes minimal casualties until committed to battle'
        : missionRole === 'EXPLOITATION'
        ? 'Exploitation forces face variable risk depending on enemy cohesion'
        : 'Supporting effort takes baseline casualty rate'
    }
  };
}

/**
 * Calculate force quality effects on casualties
 * 
 * @param {Object} forceQuality - Force quality configuration
 * @returns {Object} Force quality analysis
 */
export function calculateForceQualityEffects(forceQuality) {
  const {
    training_level = 'REGULAR',
    equipment_level = 'ADEQUATE',
    morale_level = 'NORMAL',
    experience_level = 'OVER_180_DAYS',
    technology_env = 'DRONE_CONTESTED',
    medical_capability = 'GOOD'
  } = forceQuality || {};

  // Use constants function if available
  if (calculateForceQualityModifier) {
    const analysis = calculateForceQualityModifier(forceQuality);
    return {
      ...analysis,
      source: 'combatEffectivenessModifiers.js'
    };
  }

  // Fallback calculation
  const trainingMod = TRAINING_LEVELS?.[training_level]?.casualty_multiplier || 1.0;
  const equipmentMod = EQUIPMENT_LEVELS?.[equipment_level]?.casualty_modifier || 1.0;
  const moraleMod = MORALE_LEVELS?.[morale_level]?.casualty_multiplier || 1.0;
  const expMod = EXPERIENCE_MODIFIERS?.[experience_level]?.casualty_modifier || 1.0;
  const techEnv = TECHNOLOGY_ENVIRONMENT?.[technology_env] || { rear_area_multiplier: 1.0 };
  const medCap = MEDICAL_CAPABILITY?.[medical_capability] || { dow_modifier: 1.0, rtd_improvement: 1.0 };

  const combinedCasualtyMod = trainingMod * equipmentMod * moraleMod * expMod;

  return {
    combined_casualty_multiplier: Math.round(combinedCasualtyMod * 100) / 100,
    modifiers: {
      training: { level: training_level, value: trainingMod },
      equipment: { level: equipment_level, value: equipmentMod },
      morale: { level: morale_level, value: moraleMod },
      experience: { level: experience_level, value: expMod }
    },
    technology_environment: techEnv,
    medical_capability: medCap,
    source: 'fallback calculation'
  };
}

/**
 * Calculate casualty distribution (KIA/WIA/T1-T4)
 * 
 * ESLINT-FIX: hasForwardSurgical now USED to modify survival outcomes
 * 
 * Forward Surgical Teams (FST) provide damage control surgery closer to FEBA:
 * - Reduces DOW by 15-20% (faster surgery = more survivors)
 * - Reduces T4 rate (expectant patients become treatable with faster surgery)
 * - Some T4 shift to T1/T2 (critical but salvageable with immediate surgery)
 * 
 * Source: US Army FST doctrine, Ukraine 2022-2024 stabilization point data
 * 
 * @param {number} totalCasualties - Total casualties to distribute
 * @param {Object} options - Distribution options
 * @param {string} options.mechanism - Casualty mechanism (ARTILLERY_MORTAR, FPV_DRONES, etc.)
 * @param {boolean} options.hasForwardSurgical - Whether FST is deployed forward
 * @param {number} options.goldenHourAchievement - Percentage achieving golden hour (0-1)
 * @param {Object} options.tcccEffectiveness - TCCC analysis results
 * @returns {Object} Distributed casualties
 */
export function calculateCasualtyDistribution(totalCasualties, options = {}) {
  const {
    mechanism = 'MIXED',
    hasForwardSurgical = false,
    goldenHourAchievement = 0.60,
    tcccEffectiveness = null,  // Optional TCCC analysis results
    armor_level = 'LEVEL_III',  // ENH-BAP: Body armor KIA reduction (default NATO standard)
    technologyKiaMultiplier = 1.0  // 2026-10-11: technology environment (drones) raises the immediate killed share
  } = options;

  // BCM-06 FIX: Normalize local key names to canonical keys.
  // Callers pass local keys (ARTILLERY_MORTAR, FPV_DRONES) or canonical keys (ARTILLERY, FPV_DRONE).
  const MECH_KEY_MAP = {
    ARTILLERY_MORTAR: 'ARTILLERY',
    FPV_DRONES:       'FPV_DRONE',
    SMALL_ARMS:       'SMALL_ARMS',   // same
    MINES_IED:        'MINES_IED',    // same
    OTHER:            'OTHER',        // same
    MIXED:            'ARTILLERY'     // default: most common mechanism
  };
  const canonicalMechKey = MECH_KEY_MAP[mechanism] || mechanism;
  const mechanismData = CASUALTY_MECHANISMS[canonicalMechKey] || CASUALTY_MECHANISMS.ARTILLERY;

  // Use constants distribution if available
  const distribution = CASUALTY_DISTRIBUTION || {
    kia: 0.20,
    wia: 0.80,
    t1_of_wia: 0.15,
    t2_of_wia: 0.25,
    t3_of_wia: 0.50,
    t4_of_wia: 0.10
  };

  // KIA rate adjusted by mechanism
  let kiaRate = mechanismData.kia_percentage ?? distribution.kia;  // BCM-06: .kia_rate → .kia_percentage
  
  // ESLINT-FIX: Forward Surgical Team reduces DOW (faster damage control surgery)
  // Research: FST reduces preventable deaths by 15-20%
  // Ukraine data: Forward stabilization points showed similar improvements
  let forwardSurgicalModifier = 1.0;
  if (hasForwardSurgical) {
    // BCM-13 FIX: FST_DOW_REDUCTION from casualtyCalculatorConstants.js v2.2
    forwardSurgicalModifier = FST_DOW_REDUCTION;
    kiaRate *= forwardSurgicalModifier;
  }
  
  // Golden hour achievement affects preventable deaths
  // Better golden hour = fewer DOW = lower effective KIA
  // Research: 60% golden hour achievement is baseline (1.0x)
  // Each 10% improvement reduces KIA by ~3%
  // BCM-14 FIX: All golden hour constants from casualtyCalculatorConstants.js v2.2
  const goldenHourModifier = 1.0 - ((goldenHourAchievement - GOLDEN_HOUR_BASELINE) * GOLDEN_HOUR_KIA_FACTOR);
  kiaRate *= Math.max(GOLDEN_HOUR_CLAMP_MIN, Math.min(GOLDEN_HOUR_CLAMP_MAX, goldenHourModifier));
  
  // TCCC effectiveness can further reduce mortality
  if (tcccEffectiveness?.mortality_reduction) {
    kiaRate *= (1.0 - tcccEffectiveness.mortality_reduction);
  }

  // 2026-10-11 (Dr. Tatar: educated guess): the technology environment raises the immediate killed share. Ukraine reports
  // point to a much higher killed share in a drone-saturated environment (unverified claims, dead to seriously wounded about
  // 1 to 1, versus 1 to 3 to 1 to 5 in traditional wars). Applied before body armor; capped so the share stays below 90 percent.
  if (technologyKiaMultiplier !== 1.0) {
    kiaRate = Math.min(0.9, kiaRate * technologyKiaMultiplier);
  }

  // ENH-BAP: Body armor KIA reduction
  // Applied last — armor intercepts torso penetrations after all other modifiers.
  // getArmorKIAReduction returns 0 for unknown levels (fail-safe: never inflates KIA).
  // Source: ATP 4-02.2 §3-14, Ukraine 2022-2024 data (ISW/JHU APL synthesis).
  const armorKIAReduction = getArmorKIAReduction(armor_level);
  const armorDetails = BODY_ARMOR_LEVELS[armor_level] || null;
  if (armorKIAReduction > 0) {
    kiaRate *= (1.0 - armorKIAReduction);
  }

  const wiaRate = 1.0 - kiaRate;

  // Calculate raw numbers
  const kia = Math.round(totalCasualties * kiaRate);
  const wia = totalCasualties - kia;

  // Distribute WIA into triage categories
  // Mechanism affects distribution (e.g., mines cause more T1)
  let t1Rate = mechanismData.t1_percentage ?? distribution.t1_of_wia;  // BCM-06: .t1_rate → .t1_percentage
  let t4Rate = mechanismData.t4_percentage ?? distribution.t4_of_wia;  // BCM-06: .t4_rate → .t4_percentage
  
  // ESLINT-FIX: Forward Surgical Team shifts some T4 to T1/T2
  // Patients who would be expectant (T4) become salvageable with immediate surgery
  // Research: FST converts ~25% of T4 to treatable categories
  if (hasForwardSurgical) {
    // BCM-13 FIX: FST_T4_CONVERSION + FST_T4_TO_T1_RATIO from casualtyCalculatorConstants.js v2.2
    const t4Converted = t4Rate * FST_T4_CONVERSION;   // 25% of would-be T4 become treatable
    t4Rate -= t4Converted;
    t1Rate += t4Converted * FST_T4_TO_T1_RATIO;  // 60% of converted become T1
    // Remaining 40% distributed to T2/T3 via remainder calculation below
  }
  
  // Adjust T4 based on golden hour (poor golden hour = more T4 becomes DOW)
  const adjustedT4Rate = t4Rate * (1.0 + ((1.0 - goldenHourAchievement) * 0.5));
  
  // Remaining distributed between T2 and T3
  const remainingRate = 1.0 - t1Rate - adjustedT4Rate;
  const t2Rate = remainingRate * 0.35;  // ~35% of remainder
  const t3Rate = remainingRate * 0.65;  // ~65% of remainder

  const t1 = Math.round(wia * t1Rate);
  const t2 = Math.round(wia * t2Rate);
  const t3 = Math.round(wia * t3Rate);
  const t4 = wia - t1 - t2 - t3;  // Remainder to avoid rounding errors

  return {
    total: totalCasualties,
    kia: kia,
    wia: wia,
    triage: {
      t1: Math.max(0, t1),
      t2: Math.max(0, t2),
      t3: Math.max(0, t3),
      t4: Math.max(0, t4)
    },
    rates: {
      kia_rate: Math.round(kiaRate * 100) / 100,
      wia_rate: Math.round(wiaRate * 100) / 100,
      t1_rate: Math.round(t1Rate * 100) / 100,
      t2_rate: Math.round(t2Rate * 100) / 100,
      t3_rate: Math.round(t3Rate * 100) / 100,
      t4_rate: Math.round(adjustedT4Rate * 100) / 100
    },
    mechanism: mechanism,
    golden_hour_modifier: Math.round(goldenHourModifier * 100) / 100,
    tccc_applied: tcccEffectiveness !== null,
    // ESLINT-FIX: Include forward surgical impact in output
    forward_surgical: {
      deployed: hasForwardSurgical,
      dow_reduction: hasForwardSurgical ? 0.17 : 0,  // 17% DOW reduction
      t4_conversion: hasForwardSurgical ? 0.25 : 0,  // 25% of T4 made salvageable
      modifier: forwardSurgicalModifier,
      educational_note: hasForwardSurgical 
        ? 'Forward Surgical Team deployed: 17% fewer DOW, 25% of T4 patients become salvageable'
        : 'No forward surgical capability - standard evacuation to Role 2'
    },
    // ENH-BAP: Body armor applied
    body_armor: {
      level: armor_level,
      label: armorDetails?.label || armor_level,
      kia_reduction: armorKIAReduction,
      applied: armorKIAReduction > 0,
      educational_note: armorKIAReduction > 0
        ? `${armorDetails?.short_label || armor_level} plates: ${Math.round(armorKIAReduction * 100)}% KIA reduction (torso penetrations intercepted)`
        : 'No ballistic plate protection modeled'
    }
  };
}

/**
 * Calculate rear area casualties
 * 
 * ESLINT-FIX: nationalStandard now USED to modify rear area protection
 * 
 * Different national standards have different rear area security doctrine:
 * - US: Most robust rear area operations, dedicated MP/security elements
 * - NATO: Generic baseline protection
 * - ROMANIAN: Good rear area organization, limited AD assets
 * 
 * Source: FM 3-90 Security Operations, NATO AJP-3.2, Romanian Army doctrine
 * 
 * @param {Object} config - Rear area configuration
 * @returns {Object} Rear area casualty analysis
 */
export function calculateRearAreaCasualties(config) {
  const {
    rearStrength = 1000,
    threatLevel = 'PEER',
    duration = 1,
    hasAirDefense = true,
    nationalStandard = 'NATO'
  } = config;

  // BCM-02/BCM-03: NATIONAL_REAR_AREA_MODIFIERS local constant deleted.
  // Rear area security modifier now read from NATIONAL_STANDARDS (single source of truth).
  // Supports all 7 nations: ROMANIAN=0.95, NATO=1.00, US=0.80, MULTINATIONAL=0.97,
  // GERMAN=0.90, BRITISH=0.92, POLISH=0.88 (values set in nationalStandards.js NS-02).
  // Unknown nations default to NATO baseline (1.00) — no silent data corruption.
  const rearAreaModifier = NATIONAL_STANDARDS[nationalStandard]?.rear_area_security_modifier ?? 1.00;
  const rearAreaDescription = NATIONAL_STANDARDS[nationalStandard]?.rear_area_security_description ??
    'Standard rear area protection (NATO baseline)';

  // FIX: Use getRearCasualtyRate from constants
  let baseRate = getRearCasualtyRate?.(threatLevel);
  
  // Fallback if function not available
  if (baseRate === undefined) {
    baseRate = REAR_AREA_CASUALTIES?.[threatLevel]?.base_rate ||
               THREAT_TO_REAR_MAP?.[threatLevel] ||
               0.002;
  }

  // Air defense reduces aerial threats
  const adModifier = hasAirDefense ? 0.6 : 1.0;

  // BCM-04 FIX: getHQCasualtyModifier signature is (nationalStandard, echelon).
  // Old code passed (threatLevel) as first arg → always returned undefined → fallback 1.0 fired.
  // Corps echelon used here because rear area HQ targeting affects corps-level elements.
  const hqMod = getHQCasualtyModifier(nationalStandard, 'corps');

  // BCM-03: Apply nation-specific rear area security modifier from NATIONAL_STANDARDS
  const dailyRate = baseRate * adModifier * rearAreaModifier;
  const dailyCasualties = Math.round(rearStrength * dailyRate);
  const totalCasualties = dailyCasualties * duration;

  // HQ-specific casualties (if targeting HQ)
  const hqCasualties = Math.round(dailyCasualties * 0.1 * hqMod);

  return {
    rear_strength: rearStrength,
    threat_level: threatLevel,
    base_rate: baseRate,
    ad_modifier: adModifier,
    hq_modifier: hqMod,
    // BCM-03: national_standard now reads from NATIONAL_STANDARDS (not deleted local constant)
    national_standard: {
      standard: nationalStandard,
      modifier: rearAreaModifier,
      description: rearAreaDescription
    },
    daily_rate: Math.round(dailyRate * 10000) / 10000,
    daily_casualties: dailyCasualties,
    duration: duration,
    total_casualties: totalCasualties,
    hq_casualties: hqCasualties,
    educational_note: `Rear area casualties from long-range fires, drones, and infiltration. ` +
      `${threatLevel} threat: ${Math.round(baseRate * 1000)}‰ daily rate. ` +
      `${nationalStandard} standard: ${Math.round((1 - rearAreaModifier) * 100)}% protection improvement. ` +
      (hasAirDefense ? 'Air defense reduces aerial threat by 40%.' : 'No air defense - full aerial exposure.')
  };
}

/**
 * Calculate daily battle casualties for a unit
 * 
 * BUG-034 FIX: Now normalizes posture before calculations
 * 
 * @param {Object} config - Daily battle configuration
 * @returns {Object} Daily battle casualty results
 */
/**
 * ENH-029: Derive dominant injury mechanism from tactical context.
 *
 * Consistent with getInjuryMechanismDistribution() in casualtyCalculator.js.
 * Maps terrain/missionType to the CASUALTY_MECHANISMS key that best represents
 * the KIA%, T1%, and T4% distribution for that context.
 *
 * Mapping rationale (research basis — CRE_APP_V2_20, CRE_APP_V2_44):
 *   URBAN/CITY      → SMALL_ARMS   (55% GSW urban; KIA 15%, T1 25%)
 *   ATTACK/OFFENSE  → SMALL_ARMS   (assault pattern; close combat dominates)
 *   DEFENSE         → MINES_IED    (30% mine share in def ops; KIA 22%, T1 55%)
 *   Default         → ARTILLERY_MORTAR (indirect fire dominant; previous hardcode)
 *
 * Backward-compat: any unknown terrain/missionType → 'ARTILLERY_MORTAR'.
 *
 * @param {string} terrain - Terrain key (URBAN, OPEN, MIXED, FOREST, etc.)
 * @param {string} missionType - Mission type key (MAIN_DEFENSE, DELIBERATE_ATTACK, etc.)
 * @returns {string} CASUALTY_MECHANISMS key
 */
function getDominantMechanism(terrain = 'MIXED', missionType = 'MAIN_DEFENSE') {
  // Urban terrain: small arms and fragmentation dominate, mines rare
  if (terrain === 'URBAN' || terrain === 'CITY') return 'SMALL_ARMS';

  // Offensive operations: assault infantry close combat, less mine exposure
  const OFFENSIVE_MISSIONS = [
    'DELIBERATE_ATTACK', 'HASTY_ATTACK', 'MOVEMENT_TO_CONTACT',
    'EXPLOITATION', 'PURSUIT', 'RECONNAISSANCE_IN_FORCE'
  ];
  if (OFFENSIVE_MISSIONS.includes(missionType)) return 'SMALL_ARMS';

  // Defensive operations: mine belts, IEDs, prepared obstacles dominant
  const DEFENSIVE_MISSIONS = [
    'MAIN_DEFENSE', 'AREA_DEFENSE', 'MOBILE_DEFENSE',
    'DELAY', 'RETROGRADE', 'WITHDRAWAL'
  ];
  if (DEFENSIVE_MISSIONS.includes(missionType)) return 'MINES_IED';

  // Open terrain with non-defensive posture: indirect fire dominant
  return 'ARTILLERY_MORTAR';
}

export function calculateDailyBattleCasualties(config) {
  const {
    brigade,
    day = 1,
    threatLevel = 'PEER',
    terrain = 'MIXED',
    weather = 'FAIR',
    goldenHourAchievement = 0.60,
    friendlyQuality = {},
    enemyQuality = {},
    armorLevel = 'LEVEL_III'   // ENH-BAP: Body armor level from operation_parameters
  } = config;

  const {
    strength = 4000,
    unit_type = 'MECHANIZED',
    operational_state = 'COMBAT_ENGAGED',
    posture = 'DELIBERATE_DEFENSE',
    mission_role = 'SUPPORTING',
    position          // ENH-022: carries frontage_width_km
  } = brigade || {};

  // ENH-029: Read terrain and missionType from brigade context.
  // calculator passes brigade.environment.terrain and brigade.operation.mission_type
  // but BCM previously destructured neither — terrain was always 'MIXED' and
  // missionType was hardcoded 'GROUND_COMBAT', silencing terrain multipliers and
  // terrain-defense modifiers for all defensive operations.
  const brigTerrain   = brigade?.environment?.terrain   || terrain;
  const brigMission   = brigade?.operation?.mission_type || 'MAIN_DEFENSE';

  // ENH-042: Read engagement_level from brigade.operation (set by casualtyCalculator from
  // scenario.operation_parameters.engagement_level). 'ACTIVE' was previously hardcoded —
  // not a valid ENGAGEMENT_LEVELS key, so ENGAGEMENT_LEVELS['ACTIVE']?.casualty_multiplier
  // was always undefined → fallback 1.0 fired 100%. Now uses canonical keys:
  // FULL=1.0×, SUPPORT=0.3×, RESERVE=0.0×, SECURITY=0.1×.
  const brigEngagement = brigade?.operation?.engagement_level || 'FULL';
  const brigOperationType = brigade?.operation?.operation_type;   // Tab 2 operation type, resolved by the calculator

  // BUG-034 FIX: Normalize posture
  const normalizedPosture = normalizePosture(posture, 'DELIBERATE_DEFENSE');

  // Calculate base rate with all modifiers
  const baseRateAnalysis = calculateBaseCasualtyRate({
    missionType: brigMission,      // ENH-029: was hardcoded 'GROUND_COMBAT'
    posture: normalizedPosture,    // BUG-034 FIX: Use normalized posture
    engagement: brigEngagement,    // ENH-042: was hardcoded 'ACTIVE' (invalid key → always 1.0×)
    operationType: brigOperationType,
    phaseProfile: brigade?.phase_profile || null,   // 2026-10-13: casualty table profile of the day (phaseCasualtyTable.js)
    terrain: brigTerrain,          // ENH-029: was hardcoded 'MIXED' via default
    weather: weather,
    threatLevel: threatLevel,
    unitType: unit_type,
    operationalState: operational_state,
    missionRole: mission_role,
    day: day,
    isNight: false
  });

  // Calculate force quality effects
  const qualityEffects = calculateForceQualityEffects(friendlyQuality);

  // Apply force quality to base rate
  const qualityAdjustedRate = baseRateAnalysis.adjusted_rate * qualityEffects.combined_casualty_multiplier;

  // Calculate raw casualties
  const rawCasualties = Math.round(strength * qualityAdjustedRate);

  // ENH-022: Frontage density modifier (Cat-22 Research Inventory Audit)
  // High-density formations are more vulnerable to indirect fire (58% of casualties).
  // Units with frontage_width_km = 0 (support, medical, logistics) return modifier = 1.0.
  const densityResult = getDensityModifier(strength, position?.frontage_width_km);
  const densityAdjustedCasualties = Math.round(rawCasualties * densityResult.modifier);

  // FIX: Use assessBreakProbability to check for catastrophic loss
  let breakAnalysis = null;
  if (assessBreakProbability) {
    breakAnalysis = assessBreakProbability({
      casualty_rate: qualityAdjustedRate,
      morale: friendlyQuality.morale_level || 'NORMAL',
      day: day
    });
  }

  // Distribute into categories (using density-adjusted total)
  // ENH-029: mechanism derived from terrain + missionType instead of hardcoded ARTILLERY_MORTAR.
  // KIA%, T1%, T4% now vary correctly: DEFENSE→MINES_IED (22%/55%/10%),
  // URBAN/ATTACK→SMALL_ARMS (15%/25%/2%), default→ARTILLERY_MORTAR (28%/35%/2%).
  const dominantMechanism = getDominantMechanism(brigTerrain, brigMission);
  const technologyKiaMultiplier = (() => {
    const key = friendlyQuality.technology_env;
    if (key === undefined || key === null) return 1.0;   // module called without force quality (the calculator always merges the defaults)
    const env = TECHNOLOGY_ENVIRONMENT[key];
    if (!env) {
      throw new Error(`battleCasualtyModule.calculateDailyBattleCasualties: unknown technology environment '${key}' (valid: ${Object.keys(TECHNOLOGY_ENVIRONMENT).join(', ')})`);
    }
    return env.kia_share_multiplier;
  })();
  const distribution = calculateCasualtyDistribution(densityAdjustedCasualties, {
    mechanism: dominantMechanism,
    goldenHourAchievement: goldenHourAchievement,
    armor_level: armorLevel,        // ENH-BAP
    technologyKiaMultiplier
  });

  // FIX: Calculate enemy casualties using getEnemyCasualtyModifier
  let enemyCasualties = 0;
  if (getEnemyCasualtyModifier && operational_state === 'COMBAT_ENGAGED') {
    const enemyMod = getEnemyCasualtyModifier(enemyQuality);
    // BCM-12 FIX: ENEMY_EXCHANGE_BASELINE imported from casualtyCalculatorConstants.js v2.2
    enemyCasualties = Math.round(densityAdjustedCasualties * ENEMY_EXCHANGE_BASELINE * enemyMod);
  }

  return {
    day: day,
    strength: strength,
    unit_type: unit_type,
    operational_state: operational_state,
    posture: normalizedPosture,  // BUG-034 FIX: Return normalized posture
    mission_role: mission_role,
    
    base_rate_analysis: baseRateAnalysis,
    quality_effects: qualityEffects,
    // 2026-10-21: the per-person rate behind the rounded daily total (quality and density applied, not rounded), for the sub-unit step
    casualty_rate_unrounded: qualityAdjustedRate * densityResult.modifier,
    
    rates: {
      base: baseRateAnalysis.base_rate,
      adjusted: baseRateAnalysis.adjusted_rate,
      quality_adjusted: Math.round(qualityAdjustedRate * 10000) / 10000
    },

    // ENH-022: Density modifier block for reports and debugging
    density_modifier: {
      band: densityResult.band,
      troops_per_km: densityResult.troops_per_km,
      modifier: densityResult.modifier,
      label: densityResult.label,
      applicable: densityResult.applicable,
      warning: densityResult.warning,
      raw_casualties_before_density: rawCasualties,
      casualties_after_density: densityAdjustedCasualties
    },
    
    // BUG-043 FIX: casualtyCalculator.js expects 'battle_casualties' not 'casualties'
    battle_casualties: {
      total: densityAdjustedCasualties,
      kia: distribution.kia,
      wia: distribution.wia,
      triage: distribution.triage,
      rates: distribution.rates,
      mechanism: distribution.mechanism,
      golden_hour_modifier: distribution.golden_hour_modifier,
      tccc_applied: distribution.tccc_applied,
      forward_surgical: distribution.forward_surgical
    },
    
    // Also keep 'casualties' for backward compatibility
    casualties: {
      total: densityAdjustedCasualties,
      ...distribution
    },
    
    // FIX: Include enemy casualties
    enemy_casualties: enemyCasualties,
    
    // FIX: Include break probability if available
    break_analysis: breakAnalysis,
    
    strength_after: strength - densityAdjustedCasualties,
    strength_percent: Math.round(((strength - densityAdjustedCasualties) / strength) * 100)
  };
}

/**
 * Assess MASCAL condition based on casualty load
 * 
 * BUG-044 FIX: Added defensive checks for MASCAL_THRESHOLDS structure
 * The imported constant may exist but have different property names
 * 
 * @param {Object} config - MASCAL assessment configuration
 * @returns {Object} MASCAL assessment
 */
export function assessMASCALCondition(config) {
  const {
    totalCasualties = 0,
    mtfCapacity = 100,
    evacuationCapacity = 50,
    bloodSupplyDays = 7,
    surgicalTeams = 2
  } = config;

  // BUG-044 FIX: Defensive fallback for MASCAL_THRESHOLDS
  // The constant may exist but have different structure than expected
  const DEFAULT_THRESHOLDS = {
    mtf_utilization: { warning: 0.8, critical: 0.95 },
    blood_supply: { warning: 3, critical: 1 },
    surgical_backlog: { warning: 4, critical: 8 }
  };
  
  // Build thresholds with safe property access
  const thresholds = {
    mtf_utilization: {
      warning: MASCAL_THRESHOLDS?.mtf_utilization?.warning ?? 
               MASCAL_THRESHOLDS?.MTF_UTILIZATION?.warning ??
               DEFAULT_THRESHOLDS.mtf_utilization.warning,
      critical: MASCAL_THRESHOLDS?.mtf_utilization?.critical ?? 
                MASCAL_THRESHOLDS?.MTF_UTILIZATION?.critical ??
                DEFAULT_THRESHOLDS.mtf_utilization.critical
    },
    blood_supply: {
      warning: MASCAL_THRESHOLDS?.blood_supply?.warning ?? 
               MASCAL_THRESHOLDS?.BLOOD_SUPPLY?.warning ??
               DEFAULT_THRESHOLDS.blood_supply.warning,
      critical: MASCAL_THRESHOLDS?.blood_supply?.critical ?? 
                MASCAL_THRESHOLDS?.BLOOD_SUPPLY?.critical ??
                DEFAULT_THRESHOLDS.blood_supply.critical
    },
    surgical_backlog: {
      warning: MASCAL_THRESHOLDS?.surgical_backlog?.warning ?? 
               MASCAL_THRESHOLDS?.SURGICAL_BACKLOG?.warning ??
               DEFAULT_THRESHOLDS.surgical_backlog.warning,
      critical: MASCAL_THRESHOLDS?.surgical_backlog?.critical ?? 
                MASCAL_THRESHOLDS?.SURGICAL_BACKLOG?.critical ??
                DEFAULT_THRESHOLDS.surgical_backlog.critical
    }
  };

  // Calculate utilizations
  const mtfUtilization = totalCasualties / Math.max(mtfCapacity, 1);
  const evacUtilization = totalCasualties / Math.max(evacuationCapacity, 1);
  // BCM-15 FIX: SURGICAL_RATE_OVERALL, SURGICAL_HOURS_PER_CASE, SURGICAL_HOURS_PER_TEAM_DAY
  // from casualtyCalculatorConstants.js v2.2. Function uses total aggregate (not triage breakdown).
  // If assessMASCALCondition() is refactored to accept triage data, migrate to
  // surgicalCapacity.js SURGERY_REQUIREMENTS_BY_TRIAGE (Phase 5+).
  const surgicalHoursNeeded = totalCasualties * SURGICAL_RATE_OVERALL * SURGICAL_HOURS_PER_CASE;
  const surgicalCapacity = surgicalTeams * SURGICAL_HOURS_PER_TEAM_DAY;
  const surgicalBacklog = surgicalHoursNeeded - surgicalCapacity;

  // Determine MASCAL level
  let mascalLevel = 'NORMAL';
  const triggers = [];

  if (mtfUtilization >= thresholds.mtf_utilization.critical) {
    mascalLevel = 'CRITICAL';
    triggers.push('MTF over capacity');
  } else if (mtfUtilization >= thresholds.mtf_utilization.warning) {
    mascalLevel = mascalLevel === 'NORMAL' ? 'WARNING' : mascalLevel;
    triggers.push('MTF high utilization');
  }

  if (bloodSupplyDays <= thresholds.blood_supply.critical) {
    mascalLevel = 'CRITICAL';
    triggers.push('Blood supply critical');
  } else if (bloodSupplyDays <= thresholds.blood_supply.warning) {
    mascalLevel = mascalLevel === 'NORMAL' ? 'WARNING' : mascalLevel;
    triggers.push('Blood supply low');
  }

  if (surgicalBacklog >= thresholds.surgical_backlog.critical) {
    mascalLevel = 'CRITICAL';
    triggers.push('Surgical backlog critical');
  } else if (surgicalBacklog >= thresholds.surgical_backlog.warning) {
    mascalLevel = mascalLevel === 'NORMAL' ? 'WARNING' : mascalLevel;
    triggers.push('Surgical backlog building');
  }

  return {
    mascal_level: mascalLevel,
    triggers: triggers,
    metrics: {
      mtf_utilization: Math.round(mtfUtilization * 100),
      evac_utilization: Math.round(evacUtilization * 100),
      surgical_backlog_hours: Math.max(0, Math.round(surgicalBacklog)),
      blood_supply_days: bloodSupplyDays
    },
    recommendations: mascalLevel !== 'NORMAL' ? [
      mascalLevel === 'CRITICAL' ? 'Request immediate additional medical assets' : 'Prepare for surge capacity',
      triggers.includes('Blood supply') ? 'Activate walking blood bank' : null,
      triggers.includes('Surgical') ? 'Request forward surgical team' : null
    ].filter(Boolean) : []
  };
}

/**
 * Calculate medical personnel casualties for one day.
 *
 * CALC-004 (2026-10-09): rewritten. The previous version took parameter names the caller
 * never sent (medicalStrength/roleLevel/isTargeted), looked up keys that do not exist in
 * MEDICAL_PERSONNEL_VULNERABILITY (base_rate, threat_modifiers) and returned `casualties`
 * while the caller read `medical_casualties` / `remaining_medical_personnel`, so every
 * brigade ended with undefined medic losses and a CRITICAL assessment.
 *
 * Model: medics are exposed to the same daily casualty rate as the unit they support,
 * scaled by the role's relative risk (MEDICAL_PERSONNEL_VULNERABILITY[role].base_risk_multiplier,
 * "15% higher than infantry" for the Role 1 medic). Threat level, posture, force ratio and
 * fatigue are already inside the brigade casualty rate, so they are not applied again.
 * Deliberate targeting raises the rate by the role's targeting_probability.
 *
 * @param {Object} config
 * @param {number} config.medicalPersonnel - medics alive at the start of the day
 * @param {string} config.roleType - key of MEDICAL_PERSONNEL_VULNERABILITY (e.g. 'combat_medic_role1')
 * @param {number} config.brigadeDailyCasualtyRate - battle casualties today / current strength (fraction)
 * @param {boolean} [config.isDeliberatelyTargeted=false] - scenario flag for deliberate medical targeting
 * @param {number} config.dayOfOperation - operation day (1-based)
 * @returns {Object} medical_casualties, remaining_medical_personnel, risk_analysis, educational_note
 */
export function calculateMedicalPersonnelCasualties(config) {
  const where = 'battleCasualtyModule.calculateMedicalPersonnelCasualties';
  const {
    medicalPersonnel,
    roleType,
    brigadeDailyCasualtyRate,
    isDeliberatelyTargeted = false,
    dayOfOperation
  } = config;

  if (!Number.isFinite(medicalPersonnel) || medicalPersonnel < 0) {
    throw new Error(`${where}: medicalPersonnel must be a non-negative number (got: ${medicalPersonnel}). ` +
      'Path: brigade medical personnel count');
  }
  if (!Number.isFinite(brigadeDailyCasualtyRate) || brigadeDailyCasualtyRate < 0) {
    throw new Error(`${where}: brigadeDailyCasualtyRate must be a non-negative number (got: ${brigadeDailyCasualtyRate}). ` +
      'Expected: battle casualties today divided by current strength');
  }
  if (!Number.isInteger(dayOfOperation) || dayOfOperation < 1) {
    throw new Error(`${where}: dayOfOperation must be an integer >= 1 (got: ${dayOfOperation})`);
  }
  const vulnerability = MEDICAL_PERSONNEL_VULNERABILITY[roleType];
  if (!vulnerability || !Number.isFinite(vulnerability.base_risk_multiplier)) {
    throw new Error(`${where}: roleType '${roleType}' has no base_risk_multiplier in MEDICAL_PERSONNEL_VULNERABILITY ` +
      `(valid roles: ${Object.keys(MEDICAL_PERSONNEL_VULNERABILITY).join(', ')})`);
  }
  if (isDeliberatelyTargeted && !Number.isFinite(vulnerability.targeting_probability)) {
    throw new Error(`${where}: deliberate targeting requested but role '${roleType}' defines no targeting_probability`);
  }

  const riskMultiplier = vulnerability.base_risk_multiplier;
  const targetingMultiplier = isDeliberatelyTargeted ? 1 + vulnerability.targeting_probability : 1;
  const medicCasualtyRate = brigadeDailyCasualtyRate * riskMultiplier * targetingMultiplier;
  const casualties = Math.min(medicalPersonnel, medicalPersonnel * medicCasualtyRate);
  const remaining = medicalPersonnel - casualties;

  return {
    medical_casualties: Math.round(casualties * 100) / 100,
    remaining_medical_personnel: Math.round(remaining * 100) / 100,
    risk_analysis: {
      role_type: roleType,
      day_of_operation: dayOfOperation,
      brigade_daily_casualty_rate: brigadeDailyCasualtyRate,
      risk_multiplier: riskMultiplier,
      targeting_multiplier: targetingMultiplier,
      medic_casualty_rate: medicCasualtyRate,
      is_deliberately_targeted: isDeliberatelyTargeted
    },
    educational_note: `Medical personnel (${roleType}) face ${Math.round((riskMultiplier - 1) * 100)}% ` +
      'more risk than the unit they support. ' +
      (isDeliberatelyTargeted
        ? `Deliberate targeting adds ${Math.round(vulnerability.targeting_probability * 100)}%.`
        : '')
  };
}

/**
 * BUG-031 FIX: Mapping from internal ratio keys to FORCE_SUPERIORITY constant keys
 * 
 * The calculator uses ratios like '3_TO_1', but constants use descriptive names like 'OVERWHELMING'
 * BCM-11 FIX (February 24, 2026): Updated keys to match FORCE_SUPERIORITY in
 * combatEffectivenessModifiers.js. Previous mapping used stale keys (SIGNIFICANT, SLIGHT,
 * SLIGHT_ENEMY, SIGNIFICANT_ENEMY, OVERWHELMING_ENEMY) that don't exist in the canonical
 * constant — causing getSuperiorityMod() to always fall through to the hardcoded fallback block.
 * Correct mapping: 2_TO_1→SUPERIOR, 1.5_TO_1→SUPERIOR, 1_TO_1.5→INFERIOR,
 * 1_TO_2→INFERIOR, 1_TO_3→SEVERELY_INFERIOR.
 */
const RATIO_TO_CONST_KEY = {
  '3_TO_1':   'OVERWHELMING',      // Massive qualitative advantage
  '2_TO_1':   'SUPERIOR',          // Clear advantage (was 'SIGNIFICANT' — stale)
  '1.5_TO_1': 'SUPERIOR',          // Clear advantage (was 'SLIGHT' — stale)
  '1_TO_1':   'EQUAL',             // Peer forces
  '1_TO_1.5': 'INFERIOR',          // Enemy has qualitative advantage (was 'SLIGHT_ENEMY')
  '1_TO_2':   'INFERIOR',          // Enemy has clear advantage (was 'SIGNIFICANT_ENEMY')
  '1_TO_3':   'SEVERELY_INFERIOR'  // Major capability gap (was 'OVERWHELMING_ENEMY')
};

/**
 * BUG-031 FIX: Safely get superiority modifier from FORCE_SUPERIORITY constant
 * 
 * @param {string} ratioKey - The ratio key like '1_TO_1'
 * @param {string} side - 'friendly' or 'enemy'
 * @returns {number} The modifier value, or sensible default
 */
function getSuperiorityMod(ratioKey, side = 'friendly') {
  // Map ratio key to constant key
  const constKey = RATIO_TO_CONST_KEY[ratioKey] || 'EQUAL';
  
  // Try to get from constants
  const superiorityData = FORCE_SUPERIORITY?.[constKey];
  
  if (superiorityData) {
    // Constants use 'friendly_casualty_multiplier' and 'enemy_casualty_multiplier'
    if (side === 'friendly') {
      return superiorityData.friendly_casualty_multiplier ?? 1.0;
    } else {
      return superiorityData.enemy_casualty_multiplier ?? 1.0;
    }
  }

  // BCM-11 FIX: Fallback block deleted — RATIO_TO_CONST_KEY now maps all 7 ratio
  // keys to valid FORCE_SUPERIORITY entries (OVERWHELMING/SUPERIOR/EQUAL/INFERIOR/
  // SEVERELY_INFERIOR). superiorityData will always be defined. If it somehow is not
  // (unknown ratio key), return EQUAL baseline per APP_RULES 6.1.
  throw new Error(
    `getSuperiorityMod: unknown ratioKey '${ratioKey}'. ` +
    `Valid keys: ${Object.keys(RATIO_TO_CONST_KEY).join(', ')}`
  );
}

/**
 * Apply force superiority modifier to casualties
 *
 * BUG-FSR FIX: Return object now includes `modifier: friendlyMod`.
 * Previous versions returned only `friendly_modifier`, which the calculator
 * never read — force superiority was silently inert on all prior outputs.
 *
 * ENH-026: Quality-adjusted effective strength (Dupuy CEV methodology).
 * Dupuy's research on 871 engagements (1600–1991) shows no direct cause-and-
 * effect between raw headcount ratio and exchange ratio; quality differentials
 * dominate. effectiveStrength = rawStrength × TRAINING_LEVELS[level].effectiveness_value.
 * The quality-adjusted ratio is used for ratio-category binning; raw strengths
 * are preserved in the return object for display.
 *
 * @param {Object} config - Force superiority configuration
 * @param {number} config.baseCasualties - Friendly casualties before adjustment
 * @param {number} config.friendlyStrength - Friendly headcount
 * @param {number} config.enemyStrength - Enemy headcount
 * @param {Object} [config.friendlyQuality] - Friendly force quality (training_level)
 * @param {Object} [config.enemyQuality] - Enemy force quality (training_level)
 * @returns {Object} Force superiority analysis including `modifier` field
 */
export function applyForceSuperiorityModifier(config) {
  const {
    baseCasualties = 100,
    friendlyStrength = 4000,
    enemyStrength = 4000,
    friendlyQuality = {},
    enemyQuality = {},
    countFriendlyTrainingInRatio = false
  } = config;

  // ENH-026: Compute CEV (Combat Effectiveness Value) for each side.
  // Defaults to REGULAR (1.0) when quality is absent — ratio unchanged.
  // 2026-10-11 (Dr. Tatar: each factor counts once): the friendly training level already lowers or raises the casualty
  // RATE through the combined force quality multiplier (calculateForceQualityModifier). It is not counted a second time
  // here. The enemy training (Tab 2) has no other path, so it stays in the ratio. countFriendlyTrainingInRatio = true restores
  // the old behaviour (friendly training counted in both places).
  const friendlyTrainingCEV = TRAINING_LEVELS[friendlyQuality?.training_level]?.effectiveness_value ?? 1.0;
  const friendlyCEV = countFriendlyTrainingInRatio ? friendlyTrainingCEV : 1.0;
  const enemyCEV    = TRAINING_LEVELS[enemyQuality?.training_level]?.effectiveness_value    ?? 1.0;

  // Quality-adjusted effective strengths
  const effectiveFriendly = friendlyStrength * friendlyCEV;
  const effectiveEnemy    = enemyStrength    * enemyCEV;

  // Use quality-adjusted ratio for category binning (Dupuy methodology)
  const rawRatio      = friendlyStrength  / Math.max(enemyStrength,   1);
  const adjustedRatio = effectiveFriendly / Math.max(effectiveEnemy,  1);

  // Bin by quality-adjusted ratio
  let ratioCategory;
  if      (adjustedRatio >= 2.5)  ratioCategory = '3_TO_1';
  else if (adjustedRatio >= 1.75) ratioCategory = '2_TO_1';
  else if (adjustedRatio >= 1.25) ratioCategory = '1.5_TO_1';
  else if (adjustedRatio >= 0.8)  ratioCategory = '1_TO_1';
  else if (adjustedRatio >= 0.6)  ratioCategory = '1_TO_1.5';
  else if (adjustedRatio >= 0.4)  ratioCategory = '1_TO_2';
  else                            ratioCategory = '1_TO_3';

  // BUG-031 FIX: Use helper function instead of direct access
  const friendlyMod = getSuperiorityMod(ratioCategory, 'friendly');
  const enemyMod    = getSuperiorityMod(ratioCategory, 'enemy');

  // Apply modifiers
  const adjustedFriendlyCasualties  = Math.round(baseCasualties * friendlyMod);
  const estimatedEnemyCasualties     = Math.round(baseCasualties * enemyMod);

  return {
    // BUG-FSR FIX: `modifier` is the field the calculator reads via
    // forceSuperiorityResult.modifier. Was absent in all prior versions.
    modifier: friendlyMod,

    friendly_strength: friendlyStrength,
    enemy_strength:    enemyStrength,

    // ENH-026: raw vs quality-adjusted ratio both exposed
    ratio:                       Math.round(rawRatio      * 100) / 100,
    quality_adjusted_ratio:      Math.round(adjustedRatio * 100) / 100,
    friendly_cev:                friendlyCEV,
    enemy_cev:                   enemyCEV,
    effective_friendly_strength: Math.round(effectiveFriendly),
    effective_enemy_strength:    Math.round(effectiveEnemy),

    ratio_category:   ratioCategory,
    friendly_modifier: friendlyMod,
    enemy_modifier:    enemyMod,

    base_casualties:              baseCasualties,
    adjusted_friendly_casualties: adjustedFriendlyCasualties,
    estimated_enemy_casualties:   estimatedEnemyCasualties,

    educational_note:
      `Quality-adjusted ratio ${adjustedRatio.toFixed(2)}:1 ` +
      `(raw ${rawRatio.toFixed(2)}:1, friendly CEV ${friendlyCEV}, enemy CEV ${enemyCEV}). ` +
      `Category: ${ratioCategory.replace(/_/g, ':')}. ` +
      `Friendly casualties ${Math.round((friendlyMod - 1) * 100) >= 0 ? '+' : ''}${Math.round((friendlyMod - 1) * 100)}%. ` +
      `Enemy casualties ${Math.round((enemyMod - 1) * 100) >= 0 ? '+' : ''}${Math.round((enemyMod - 1) * 100)}%.`
  };
}



/**
 * Calculate unit type casualties with posture-specific modifiers
 * 
 * FIX-BCM-002: Now uses posture parameter for attacker/defender differentiation
 * BUG-034 FIX: Now normalizes posture before lookup
 * 
 * @param {Object} config - Unit type casualty configuration
 * @returns {Object} Unit type specific casualty analysis
 */
export function calculateUnitTypeCasualties(config) {
  const {
    unitType = 'MECHANIZED',
    strength = 1000,
    baseCasualtyRate = 0.02,
    terrain = 'MIXED',
    posture = 'DELIBERATE_DEFENSE'  // FIX-BCM-002: Added posture parameter
  } = config;

  // BUG-034 FIX: Normalize posture before lookup
  const normalizedPosture = normalizePosture(posture, 'DELIBERATE_DEFENSE');

  // Get unit type modifier
  const unitTypeMod = getUnitTypeCasualtyModifier?.(unitType) || 
                     UNIT_TYPES?.[unitType]?.casualty_modifier || 1.0;

  // Get terrain modifier for this unit type
  const terrainMod = UNIT_TYPES?.[unitType]?.terrain_modifiers?.[terrain] || 1.0;

  // BCM-09: Use OPERATIONAL_POSTURES (already imported from operationsConfig.js) as sole
  // source for posture modifier. Deleted local postureModifiers block — it had DIFFERENT
  // values from canonical (e.g. DELIBERATE_DEFENSE: 0.7 local vs 0.6 canonical,
  // RETIREMENT: 0.5 local vs 0.1 canonical, RESERVE: 0.2 local vs 0.0 canonical).
  // Unknown posture → 1.0 (safe default, function still returns a result).
  const postureMod = OPERATIONAL_POSTURES?.[normalizedPosture]?.casualty_multiplier ?? 1.0;

  // Calculate adjusted rate
  const adjustedRate = baseCasualtyRate * unitTypeMod * terrainMod * postureMod;
  const casualties = Math.round(strength * adjustedRate);

  return {
    unit_type: unitType,
    strength: strength,
    base_rate: baseCasualtyRate,
    unit_type_modifier: unitTypeMod,
    terrain_modifier: terrainMod,
    posture: normalizedPosture,  // BUG-034 FIX: Return normalized posture
    posture_modifier: postureMod,
    adjusted_rate: Math.round(adjustedRate * 10000) / 10000,
    casualties: casualties,
    educational_note: `${unitType} units in ${normalizedPosture} posture at ${Math.round(unitTypeMod * 100)}% baseline rate. ` +
      `Posture modifier: ${Math.round(postureMod * 100)}%. ${terrain} terrain: ${Math.round((terrainMod - 1) * 100)}% modifier.`
  };
}

/**
 * Calculate echelon-based casualty distribution
 * 
 * Uses getEchelonBreakdown to distribute casualties across echelon levels
 * based on national standard doctrine.
 * 
 * @param {Object} config - Echelon distribution configuration
 * @returns {Object} Echelon-based casualty distribution
 */
export function calculateEchelonCasualties(config) {
  const {
    totalCasualties = 0,
    echelon = 'BRIGADE',
    nationalStandard = 'NATO',
    threatLevel = 'PEER'
  } = config;
  
  // FIX: Use getEchelonBreakdown from nationalStandards.js
  let echelonBreakdown;
  if (getEchelonBreakdown) {
    echelonBreakdown = getEchelonBreakdown(nationalStandard, echelon);
  } else {
    // Fallback breakdown
    echelonBreakdown = {
      BATTALION: {
        forward_units: 0.70,    // 70% casualties at forward units
        support_units: 0.20,    // 20% at support/CSS
        hq_elements: 0.10       // 10% at HQ
      },
      BRIGADE: {
        forward_battalions: 0.65,
        support_battalion: 0.25,
        brigade_hq: 0.10
      },
      DIVISION: {
        maneuver_brigades: 0.60,
        support_brigade: 0.25,
        division_troops: 0.15
      }
    };
  }
  
  const breakdown = echelonBreakdown?.[echelon] || echelonBreakdown?.BRIGADE;
  
  // FIX: Adjust HQ casualty ratio based on threat level
  // Ukraine 2022-2024: Peer adversaries actively target HQ elements via signals/drones
  const hqThreatModifier = threatLevel === 'PEER' ? 1.5 :
                          threatLevel === 'HIGH' ? 1.3 :
                          threatLevel === 'SEVERE' ? 1.8 : 1.0;
  
  // Calculate distribution
  const distribution = {};
  let total = 0;
  let adjustedBreakdown = { ...breakdown };
  
  // Apply HQ threat modifier if HQ element exists
  const hqKey = Object.keys(adjustedBreakdown || {}).find(k => 
    k.toLowerCase().includes('hq') || k.toLowerCase().includes('headquarters')
  );
  
  if (hqKey && hqThreatModifier > 1.0) {
    const originalHqRatio = adjustedBreakdown[hqKey];
    const hqIncrease = originalHqRatio * (hqThreatModifier - 1.0);
    adjustedBreakdown[hqKey] = originalHqRatio + hqIncrease;
    
    // Reduce other elements proportionally to maintain ~100% total
    const otherKeys = Object.keys(adjustedBreakdown).filter(k => k !== hqKey);
    const reductionPerElement = hqIncrease / otherKeys.length;
    otherKeys.forEach(k => {
      adjustedBreakdown[k] = Math.max(0.05, adjustedBreakdown[k] - reductionPerElement);
    });
  }
  
  for (const [element, ratio] of Object.entries(adjustedBreakdown || {})) {
    const elementCasualties = Math.round(totalCasualties * ratio);
    distribution[element] = {
      casualties: elementCasualties,
      percentage: Math.round(ratio * 100)
    };
    total += elementCasualties;
  }
  
  // Handle rounding differences
  if (total !== totalCasualties && Object.keys(distribution).length > 0) {
    const firstKey = Object.keys(distribution)[0];
    distribution[firstKey].casualties += (totalCasualties - total);
  }
  
  return {
    total_casualties: totalCasualties,
    echelon: echelon,
    national_standard: nationalStandard,
    threat_level: threatLevel,
    hq_threat_modifier: hqThreatModifier,
    distribution: distribution,
    educational_note: `${echelon}-level casualties distributed per ${nationalStandard} doctrine. ` +
      `Forward/maneuver units typically absorb 60-70% of casualties.` +
      (hqThreatModifier > 1.0 ? ` HQ casualties increased ${Math.round((hqThreatModifier - 1) * 100)}% due to ${threatLevel} threat targeting.` : '')
  };
}

// ============================================================================
// EXPORTS
// ============================================================================

export {
  CASUALTY_MECHANISMS,
  NIGHT_BATTLE_MODIFIERS    // BCM-07: re-exported (imported from casualtyCalculatorConstants.js)
  // URBAN_WARFARE_MODIFIERS removed — BCM-08: dead code, zero usage sites
  // Note: MISSION_ROLES and getMissionRoleMultiplier re-exported at top of CONSTANTS section
  // (imported from operationsConfig.js — single source of truth since BCM-10)
};

const battleCasualtyModule = {
  // BUG-034 FIX: Export normalizePosture for other modules
  normalizePosture,
  
  // Original functions
  getEnhancedUnitType,
  calculateBaseCasualtyRate,
  calculateForceQualityEffects,
  calculateCasualtyDistribution,
  calculateRearAreaCasualties,
  calculateDailyBattleCasualties,
  
  // New functions (unique to this module)
  assessMASCALCondition,              // Uses MASCAL_THRESHOLDS
  calculateMedicalPersonnelCasualties, // Uses MEDICAL_PERSONNEL_VULNERABILITY
  applyForceSuperiorityModifier,       // Uses FORCE_SUPERIORITY
  calculateUnitTypeCasualties,         // Uses getUnitTypeCasualtyModifier
  calculateEchelonCasualties,          // Uses getEchelonBreakdown
  
  // BUG-002 FIX: Mission role function
  getMissionRoleMultiplier,
  
  // Constants
  CASUALTY_MECHANISMS,
  MISSION_ROLES  // BUG-002 FIX: Include in default export
};

export default battleCasualtyModule;
